import { skipToken } from '@reduxjs/toolkit/query';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { CLAIMS_FILING_FEATURE_FLAGS } from '@/components/claims-filing/constants';
import { MakeCallModal } from '@/components/common/calls/make-call-modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useGetBlandCallByIdQuery,
  useGetBlandCallsQuery,
} from '@/services/api/blandCallService';
import { useGetTaskRunsQuery } from '@/services/api/blandService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import type { Task } from '@/services/types/client-intake-types';
import type { CoverageSchema } from '@/services/types/coverage-types';
import { CoverageCategory } from '@/services/types/coverage-types';
import { CanonicalTaskType } from '@/services/types/task-types';
import type { VoiceAIAgentRunSchema } from '@/types/enhanced-bland-call';
import { EnhancedBlandWorkflowStatus } from '@/types/enhanced-bland-call';

import { ClaimCallBasic } from './ClaimCallBasic';
import { ClaimCallDetails } from './ClaimCallDetails';

export type CallStatus =
  | 'idle'
  | 'in_progress'
  | 'success'
  | 'failed_terminal'
  | 'failed'
  | 'waiting_info';

type PhoneRow = {
  label: string;
  phone: string;
  source: 'carrier' | 'adjuster_coverage' | 'adjuster_manual';
};

interface ClaimCallProps {
  linkedCoverage?: CoverageSchema;
  blandCallId?: string;
  taskId?: string;
  matterId?: string;
  callStatus: CallStatus;
  isCallOutcomeReady: boolean;
  onRetryCall: () => void;
  onBackToReview: () => void;
  onSendToBpo?: () => void;
  isStartingCall?: boolean;
  startCallError?: string | null;
}

export const ClaimCall: React.FC<ClaimCallProps> = ({
  linkedCoverage,
  blandCallId,
  taskId,
  matterId,
  callStatus,
  isCallOutcomeReady,
  onRetryCall,
  onBackToReview,
  onSendToBpo,
  isStartingCall,
  startCallError,
}) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [prefillPhone, setPrefillPhone] = useState<string | undefined>(
    undefined,
  );
  const [prefillLabel, setPrefillLabel] = useState<string | undefined>(
    undefined,
  );

  const openCallModalWith = (phone?: string, label?: string) => {
    if (!phone) return;
    const hasCarrierHandles =
      (linkedCoverage?.carrier?.handles || []).length > 0;
    if (!hasCarrierHandles) {
      toast.warning('Missing carrier contact', {
        description:
          'Attach a carrier contact handle to this coverage before calling. Use the Carrier Directory to select the correct division and handles.',
      });
    }
    setPrefillPhone(phone);
    setPrefillLabel(label);
    setIsCallModalOpen(true);
  };

  const carrierName = linkedCoverage?.carrier?.name ?? 'Unknown carrier';
  const carrierHandles = linkedCoverage?.carrier?.handles || [];
  const phoneRows: PhoneRow[] = [];
  carrierHandles
    .filter(
      (h) =>
        h.value &&
        h.handle_type &&
        h.handle_type.toLowerCase().includes('phone'),
    )
    .slice(0, 1)
    .forEach((h) => {
      phoneRows.push({ label: carrierName, phone: h.value, source: 'carrier' });
    });

  // Fetch minimal call data to allow concise success/failed summary
  const { data: scopedCall } = useGetBlandCallByIdQuery(
    blandCallId || skipToken,
  );
  const { data: blandCalls = [] } = useGetBlandCallsQuery();

  const latestBland = useMemo(() => {
    if (blandCallId) {
      return scopedCall || blandCalls.find((c) => c.id === blandCallId) || null;
    }
    if (taskId) {
      return (
        blandCalls
          .filter((c) => c.task?.id === taskId)
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )[0] || null
      );
    }
    const covId = linkedCoverage?.id;
    if (covId) {
      return (
        blandCalls
          .filter((c) => c.task?.coverage?.id === covId)
          .sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          )[0] || null
      );
    }
    return null;
  }, [scopedCall, blandCalls, blandCallId, taskId, linkedCoverage?.id]);

  // Discover a task id to associate runs with (prefer from latest call record)
  const { data: tasksResponse } = useGetTasksQuery(
    matterId
      ? { matter: matterId, limit: 200 }
      : (skipToken as typeof skipToken),
    { skip: !matterId },
  );
  const openClaimTask: Task | undefined = useMemo(() => {
    if (!tasksResponse?.results) return undefined;
    const tasks = tasksResponse.results;
    const coverageId = linkedCoverage?.id;
    const desiredTaskType =
      linkedCoverage?.category === CoverageCategory.PROPERTY
        ? CanonicalTaskType.OPEN_PREMISES_CLAIM
        : CanonicalTaskType.OPEN_CLAIM;

    return tasks.find(
      (t) =>
        t.coverage_id === coverageId &&
        t.type?.canonical_type === desiredTaskType,
    );
  }, [tasksResponse, linkedCoverage?.category, linkedCoverage?.id]);
  const openClaimTaskId = openClaimTask?.id;

  // Choose target task id for runs: latest bland's task id > provided taskId prop > discovered task
  const taskIdForRuns = useMemo(() => {
    const fromCall = (latestBland as { task?: { id?: string } } | null)?.task
      ?.id as string | undefined;
    if (fromCall) return fromCall;
    if (taskId) return taskId;
    return openClaimTaskId;
  }, [latestBland, taskId, openClaimTaskId]);

  // Fetch runs for the selected task to surface failures even in basic view
  const { data: runsData } = useGetTaskRunsQuery(
    matterId && taskIdForRuns
      ? { matterId, taskId: taskIdForRuns }
      : (skipToken as typeof skipToken),
  );
  const lastRun: VoiceAIAgentRunSchema | null = useMemo(() => {
    const runs = runsData?.runs || [];
    if (!runs.length) return null;
    return runs
      .slice()
      .sort(
        (a, b) =>
          new Date(b.created_at || '').getTime() -
          new Date(a.created_at || '').getTime(),
      )[0];
  }, [runsData]);
  const runsCount = runsData?.count || 0;

  // Auto-open details on failure to mirror interaction patterns elsewhere
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    const status = lastRun?.status as EnhancedBlandWorkflowStatus | undefined;
    const failedStatuses: EnhancedBlandWorkflowStatus[] = [
      EnhancedBlandWorkflowStatus.FAILED,
      EnhancedBlandWorkflowStatus.FAILED_INELIGIBLE,
      EnhancedBlandWorkflowStatus.FAILED_READY_CHECK,
      EnhancedBlandWorkflowStatus.CANCELLED,
    ];
    const hasFailure =
      !!lastRun?.error_message ||
      (status ? failedStatuses.includes(status) : false);
    if (hasFailure) {
      setIsDetailsOpen(true);
      autoOpenedRef.current = true;
    }
  }, [lastRun?.status, lastRun?.error_message]);

  const statusPillClass = (() => {
    if (callStatus === 'success')
      return 'text-green-700 bg-green-50 border-green-200';
    if (callStatus === 'in_progress')
      return 'text-blue-700 bg-blue-50 border-blue-200';
    if (callStatus === 'waiting_info')
      return 'text-amber-800 bg-amber-50 border-amber-200';
    if (callStatus === 'failed' || callStatus === 'failed_terminal')
      return 'text-red-700 bg-red-50 border-red-200';
    return 'text-gray-700 bg-gray-50 border-gray-200';
  })();

  const statusPillText = (() => {
    if (callStatus === 'idle') return 'idle';
    if (callStatus === 'in_progress') return 'in progress';
    if (callStatus === 'success') return 'success';
    if (callStatus === 'failed_terminal' || callStatus === 'failed')
      return 'didn’t complete';
    return callStatus.replaceAll('_', ' ');
  })();

  // Disable conditions simplified: 3P feature gate, or no task available
  const is3PDisabled =
    linkedCoverage?.category === CoverageCategory.THIRD_PARTY &&
    !CLAIMS_FILING_FEATURE_FLAGS.enableVoiceAIForThirdParty;

  const hasTaskForCoverage =
    !!linkedCoverage?.id &&
    (tasksResponse?.results ?? []).some(
      (t) =>
        t.coverage_id === linkedCoverage.id &&
        t.type?.canonical_type ===
          (linkedCoverage.category === CoverageCategory.PROPERTY
            ? CanonicalTaskType.OPEN_PREMISES_CLAIM
            : CanonicalTaskType.OPEN_CLAIM),
    );
  const noTaskDisabled = tasksResponse !== undefined && !hasTaskForCoverage;

  let isVoiceAIDisabled = false;
  let voiceAIDisabledReason: string | undefined;
  if (is3PDisabled) {
    isVoiceAIDisabled = true;
    voiceAIDisabledReason =
      'Voice AI Call is not live for 3P coverages yet. Please call the carrier manually.';
  } else if (noTaskDisabled) {
    isVoiceAIDisabled = true;
    voiceAIDisabledReason =
      'No eligible task found for this coverage. Please call the carrier manually.';
  }

  return (
    <div className="mx-auto max-w-7xl w-full p-4" aria-live="polite">
      {linkedCoverage &&
        (linkedCoverage.carrier?.handles || []).length === 0 && (
          <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3">
            <div className="text-sm font-semibold text-red-800">
              Missing carrier contact
            </div>
            <div className="text-xs text-red-700 mt-1">
              Attach a carrier contact handle (phone, email, fax, website) to
              this carrier. Use the Carrier Directory to select the correct
              division and handles.
            </div>
          </div>
        )}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <CardTitle className="text-base">Voice AI Call</CardTitle>
              {lastRun?.updated_at && (
                <span className="text-[11px] text-gray-500">
                  Last updated {new Date(lastRun.updated_at).toLocaleString()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`text-[11px] px-2 py-0.5 rounded-full border ${statusPillClass}`}
                title={statusPillText}
              >
                {statusPillText}
              </span>
              <button
                type="button"
                className="relative text-xs text-gray-600 hover:text-gray-900 underline-offset-2 hover:underline"
                aria-label={isDetailsOpen ? 'Hide details' : 'Show details'}
                aria-expanded={isDetailsOpen}
                onClick={() => setIsDetailsOpen((v) => !v)}
              >
                {isDetailsOpen ? 'Hide details' : 'Show details'}
                {runsCount > 0 && !isDetailsOpen && (
                  <span
                    className="ml-1 inline-flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] px-1.5 h-4 min-w-4"
                    aria-label={`There are ${runsCount} runs`}
                  >
                    {runsCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <ClaimCallBasic
            callStatus={callStatus}
            isCallOutcomeReady={isCallOutcomeReady}
            latestBland={latestBland}
            startCallError={startCallError}
            isStartingCall={isStartingCall}
            onRetryCall={onRetryCall}
            onBackToReview={onBackToReview}
            onManualCall={() =>
              openCallModalWith(phoneRows[0]?.phone, phoneRows[0]?.label)
            }
            manualCallAvailable={phoneRows.length > 0}
            lastRunError={lastRun?.error_message || null}
            isVoiceAIDisabled={isVoiceAIDisabled}
            voiceAIDisabledReason={
              isVoiceAIDisabled ? voiceAIDisabledReason : undefined
            }
          />

          {isDetailsOpen && (
            <ClaimCallDetails
              matterId={matterId}
              taskIdForRuns={taskIdForRuns}
              latestBland={latestBland}
              phoneRows={phoneRows}
              onManualCall={openCallModalWith}
              onSendToBpo={onSendToBpo}
            />
          )}
        </CardContent>
      </Card>

      <MakeCallModal
        isOpen={isCallModalOpen}
        onClose={() => setIsCallModalOpen(false)}
        initialPhoneNumber={prefillPhone}
        initialContactLabel={prefillLabel}
        initialContactPhone={prefillPhone}
        source="claims_filing"
      />

    </div>
  );
};
